import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { toastSuccess, toastError } from './ui/toast';
import CipherHeader from './components/ui/CipherHeader';
import { colors as theme } from './theme';

const brand = {
  gold: theme.gold,
  dark: theme.bg,
  mid: theme.surface1,
  light: theme.surface3,
  silver: theme.textMuted,
  white: theme.text,
};

const ProgramQuickEdit = ({ programId, onBack }) => {
  const [programName, setProgramName] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [exerciseMap, setExerciseMap] = useState({}); // id → exercise object
  const [categoryMap, setCategoryMap] = useState({}); // category → exercise[]
  const [swaps, setSwaps] = useState({}); // { [blockItemId]: newExerciseId }
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (programId) {
      loadProgram();
      loadExercises();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const loadExercises = async () => {
    const { data } = await supabase
      .from('exercises')
      .select('id, name, movement_category, exercise_type')
      .order('name');
    if (!data) return;

    const map = {};
    const catMap = {};
    data.forEach(ex => {
      map[ex.id] = ex;
      const cat = ex.movement_category || 'Other';
      if (!catMap[cat]) catMap[cat] = [];
      catMap[cat].push(ex);
    });
    setAllExercises(data);
    setExerciseMap(map);
    setCategoryMap(catMap);
  };

  const loadProgram = async () => {
    setLoading(true);
    try {
      const { data: prog } = await supabase
        .from('programs')
        .select('name, description')
        .eq('id', programId)
        .single();
      setProgramName(prog?.name || '');

      const { data: dbWeeks } = await supabase
        .from('program_weeks')
        .select(`
          id, week_number, name,
          program_days (
            id, name, day_number,
            exercise_blocks (
              id, block_order, rest_seconds,
              block_items (
                id, exercise_id, position, sets, reps
              )
            )
          )
        `)
        .eq('program_id', programId)
        .order('week_number');

      const mapped = (dbWeeks || []).map(w => ({
        id: w.id,
        week_number: w.week_number,
        name: w.name,
        days: (w.program_days || [])
          .sort((a, b) => a.day_number - b.day_number)
          .map(d => ({
            id: d.id,
            name: d.name,
            day_number: d.day_number,
            blocks: (d.exercise_blocks || [])
              .sort((a, b) => a.block_order - b.block_order)
              .map(b => ({
                id: b.id,
                block_order: b.block_order,
                rest_seconds: b.rest_seconds,
                items: (b.block_items || [])
                  .sort((a, b) => a.position - b.position),
              })),
          })),
      }));

      setWeeks(mapped);
      if (mapped.length > 0) {
        setExpandedWeek(mapped[0].id);
        if (mapped[0].days.length > 0) setExpandedDay(mapped[0].days[0].id);
      }
    } catch (err) {
      console.error(err);
      toastError('Failed to load program');
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = (blockItemId, newExerciseId) => {
    setSwaps(prev => ({ ...prev, [blockItemId]: parseInt(newExerciseId) }));
  };

  const handleSave = async () => {
    if (!programName.trim()) {
      toastError('Program name cannot be empty');
      return;
    }
    setSaving(true);
    try {
      // Save name
      await supabase
        .from('programs')
        .update({ name: programName.trim() })
        .eq('id', programId);

      // Save swapped exercises
      const swapEntries = Object.entries(swaps);
      for (const [blockItemId, newExerciseId] of swapEntries) {
        await supabase
          .from('block_items')
          .update({ exercise_id: newExerciseId })
          .eq('id', parseInt(blockItemId));
      }

      toastSuccess('Program saved successfully');
      setSwaps({});
      await loadProgram();
    } catch (err) {
      console.error(err);
      toastError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const getExerciseId = (item) => swaps[item.id] ?? item.exercise_id;

  const getCategory = (exerciseId) => {
    return exerciseMap[exerciseId]?.movement_category || null;
  };

  // Same-category exercises first (the useful "alternatives"), then every other
  // exercise under a divider — so the swap list is never limited to one
  // movement category and there's no exercise you can't pick.
  const getOptionsForItem = (item) => {
    const cat = getCategory(getExerciseId(item));
    const same = cat ? (categoryMap[cat] || []) : [];
    const sameIds = new Set(same.map(e => e.id));
    const others = allExercises.filter(e => !sameIds.has(e.id));
    return { cat, same, others };
  };

  // eslint-disable-next-line no-unused-vars
  const hasChanges = programName !== (weeks.length ? weeks[0]?.name : '') || Object.keys(swaps).length > 0;
  const swapCount = Object.keys(swaps).length;

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: brand.silver, backgroundColor: brand.dark, minHeight: '100vh' }}>
        Loading program...
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: brand.dark, minHeight: '100vh', paddingBottom: 60 }}>
      <CipherHeader
        title="Edit Program"
        subtitle="Rename or swap exercises"
        onBack={onBack}
      />

      <div style={{ padding: '0 20px', maxWidth: 720, margin: '0 auto' }}>

        {/* Program Name */}
        <div style={{
          backgroundColor: brand.mid,
          border: `1px solid ${brand.light}`,
          borderRadius: 14,
          padding: '20px',
          marginBottom: 20,
        }}>
          <label style={{ color: brand.silver, fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
            Program Name
          </label>
          <input
            value={programName}
            onChange={e => setProgramName(e.target.value)}
            style={{
              width: '100%',
              backgroundColor: brand.light,
              border: `1px solid ${brand.gold}`,
              borderRadius: 8,
              padding: '12px 14px',
              color: brand.white,
              fontSize: 16,
              fontWeight: 600,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Save Bar */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: brand.dark,
          padding: '10px 0',
          marginBottom: 10,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          borderBottom: `1px solid ${brand.light}`,
        }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1,
              padding: '13px',
              backgroundColor: brand.gold,
              border: 'none',
              borderRadius: 8,
              color: brand.dark,
              fontSize: 15,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : swapCount > 0 ? `Save Changes (${swapCount} swap${swapCount !== 1 ? 's' : ''})` : 'Save Changes'}
          </button>
          {swapCount > 0 && (
            <button
              onClick={() => setSwaps({})}
              style={{
                padding: '13px 16px',
                backgroundColor: 'transparent',
                border: `1px solid ${brand.light}`,
                borderRadius: 8,
                color: brand.silver,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Reset
            </button>
          )}
        </div>

        {/* Weeks */}
        {weeks.map(week => (
          <div key={week.id} style={{ marginBottom: 12 }}>
            {/* Week Header */}
            <button
              onClick={() => setExpandedWeek(expandedWeek === week.id ? null : week.id)}
              style={{
                width: '100%',
                backgroundColor: expandedWeek === week.id ? brand.light : brand.mid,
                border: `1px solid ${expandedWeek === week.id ? brand.gold : brand.light}`,
                borderRadius: expandedWeek === week.id ? '10px 10px 0 0' : 10,
                padding: '14px 18px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                color: brand.white,
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 15 }}>
                Week {week.week_number} — {week.name}
              </span>
              <span style={{ color: brand.silver, fontSize: 18 }}>
                {expandedWeek === week.id ? '▲' : '▼'}
              </span>
            </button>

            {/* Week Content */}
            {expandedWeek === week.id && (
              <div style={{
                border: `1px solid ${brand.gold}`,
                borderTop: 'none',
                borderRadius: '0 0 10px 10px',
                overflow: 'hidden',
              }}>
                {week.days.map((day, di) => (
                  <div key={day.id} style={{ borderBottom: di < week.days.length - 1 ? `1px solid ${brand.light}` : 'none' }}>
                    {/* Day Header */}
                    <button
                      onClick={() => setExpandedDay(expandedDay === day.id ? null : day.id)}
                      style={{
                        width: '100%',
                        backgroundColor: expandedDay === day.id ? 'rgba(212,175,55,0.08)' : 'transparent',
                        border: 'none',
                        padding: '12px 18px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: brand.white,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          backgroundColor: brand.light,
                          color: brand.gold,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: 4,
                          letterSpacing: 0.5,
                        }}>
                          DAY {day.day_number}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{day.name}</span>
                        <span style={{ color: brand.silver, fontSize: 12 }}>
                          {day.blocks.reduce((sum, b) => sum + b.items.length, 0)} exercises
                        </span>
                      </div>
                      <span style={{ color: brand.silver, fontSize: 14 }}>
                        {expandedDay === day.id ? '▲' : '▼'}
                      </span>
                    </button>

                    {/* Exercise List */}
                    {expandedDay === day.id && (
                      <div style={{ padding: '0 16px 16px' }}>
                        {day.blocks.map((block, bi) =>
                          block.items.map((item, ii) => {
                            const currentExId = getExerciseId(item);
                            const currentEx = exerciseMap[currentExId];
                            const isSwapped = swaps[item.id] !== undefined;
                            const options = getOptionsForItem(item);
                            const category = getCategory(currentExId);

                            return (
                              <div
                                key={item.id}
                                style={{
                                  backgroundColor: isSwapped ? 'rgba(212,175,55,0.06)' : brand.light,
                                  border: `1px solid ${isSwapped ? brand.gold : brand.light}`,
                                  borderRadius: 8,
                                  padding: '12px 14px',
                                  marginBottom: 8,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 8,
                                }}
                              >
                                {/* Exercise info row */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ color: brand.white, fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {currentEx?.name || `Exercise #${currentExId}`}
                                      {isSwapped && (
                                        <span style={{ marginLeft: 6, color: brand.gold, fontSize: 11, fontWeight: 700 }}>SWAPPED</span>
                                      )}
                                    </div>
                                    <div style={{ color: brand.silver, fontSize: 12, marginTop: 2, display: 'flex', gap: 10 }}>
                                      <span>{item.sets} sets × {item.reps} reps</span>
                                      {item.rest_seconds && <span>{block.rest_seconds}s rest</span>}
                                      {category && <span style={{ color: 'rgba(212,175,55,0.6)' }}>{category}</span>}
                                    </div>
                                  </div>
                                </div>

                                {/* Swap dropdown */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ color: brand.silver, fontSize: 12, whiteSpace: 'nowrap' }}>Swap:</span>
                                  <select
                                    value={currentExId}
                                    onChange={e => handleSwap(item.id, e.target.value)}
                                    style={{
                                      flex: 1,
                                      backgroundColor: brand.dark,
                                      border: `1px solid ${isSwapped ? brand.gold : brand.light}`,
                                      borderRadius: 8,
                                      padding: '7px 10px',
                                      color: brand.white,
                                      fontSize: 13,
                                      cursor: 'pointer',
                                      outline: 'none',
                                    }}
                                  >
                                    {options.same.length > 0 && (
                                      <optgroup label={options.cat ? `Similar — ${options.cat}` : 'Similar'}>
                                        {options.same.map(ex => (
                                          <option key={ex.id} value={ex.id}>{ex.name}</option>
                                        ))}
                                      </optgroup>
                                    )}
                                    <optgroup label={options.same.length > 0 ? 'All other exercises' : 'All exercises'}>
                                      {options.others.map(ex => (
                                        <option key={ex.id} value={ex.id}>{ex.name}</option>
                                      ))}
                                    </optgroup>
                                  </select>
                                  {isSwapped && (
                                    <button
                                      onClick={() => {
                                        setSwaps(prev => {
                                          const next = { ...prev };
                                          delete next[item.id];
                                          return next;
                                        });
                                      }}
                                      style={{
                                        backgroundColor: 'transparent',
                                        border: `1px solid ${brand.light}`,
                                        borderRadius: 8,
                                        color: brand.silver,
                                        padding: '7px 10px',
                                        fontSize: 12,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Undo
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                        {day.blocks.every(b => b.items.length === 0) && (
                          <div style={{ color: brand.silver, fontSize: 13, padding: '8px 0' }}>No exercises on this day.</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgramQuickEdit;
